import { useCallback, useMemo, useState } from "react";
import type { SessionClientFilters } from "../utils/bookingFormAuditSession";

export const BOOKING_SOURCE_LABELS: Record<string, string> = {
  "southampton-port-taxi": "Southampton Port Taxi",
  "airport-transfers-taxi": "Airport Transfers Taxi",
  "uk-airport-taxi": "UK Airport Taxi",
  "liverpool-dock-taxi": "Liverpool Docks Taxi",
  "tikla-cars": "Tikla Cars",
};

/** Default rows per page for the Website Audit session list. */
export const SESSION_PAGE_SIZE = 25;

export function useBookingFormAuditQuery() {
  const [bookingSource, setBookingSource] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const buildQuery = useCallback(
    (options?: {
      includeSessionLimit?: boolean;
      limit?: number;
      offset?: number;
      clientFilters?: SessionClientFilters;
    }) => {
      const params = new URLSearchParams();
      if (bookingSource) params.set("bookingSource", bookingSource);
      if (customerEmail) params.set("customerEmail", customerEmail);
      if (sessionId) params.set("sessionId", sessionId);
      if (fromDate) params.set("from", new Date(fromDate).toISOString());
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        params.set("to", end.toISOString());
      }

      const filters = options?.clientFilters;
      if (filters) {
        if (filters.audience && filters.audience !== "all") {
          params.set("audience", filters.audience);
        }
        if (filters.status !== "all") params.set("status", filters.status);
        if (filters.lastStep) params.set("lastStep", filters.lastStep);
        if (filters.reachedStep) params.set("reachedStep", filters.reachedStep);
        if (filters.search.trim()) params.set("search", filters.search.trim());
        if (filters.landingPage.trim()) {
          params.set("landingPage", filters.landingPage.trim());
        }
        if (filters.trafficSource) {
          params.set("trafficSource", filters.trafficSource);
        }
        if (filters.deviceType) params.set("deviceType", filters.deviceType);
        if (filters.country) params.set("country", filters.country);
        if (filters.browser) params.set("browser", filters.browser);
        if (filters.cookieConsent) params.set("cookieConsent", "true");
        if (filters.hadValidationError) {
          params.set("hadValidationError", "true");
        }
        if (filters.hadFareQuote) params.set("hadFareQuote", "true");
        if (filters.hadFareQuoteFailure) {
          params.set("hadFareQuoteFailure", "true");
        }
        if (filters.hadPaymentFailure) {
          params.set("hadPaymentFailure", "true");
        }
        if (filters.hadBookingFailure) {
          params.set("hadBookingFailure", "true");
        }
        if (filters.reachedPayment) params.set("reachedPayment", "true");
        if (filters.cardEntry) params.set("cardEntry", filters.cardEntry);
        if (filters.contact === "has_email") {
          params.set("contact", filters.contact);
        }
      }

      if (options?.includeSessionLimit !== false) {
        params.set(
          "limit",
          String(options?.limit ?? SESSION_PAGE_SIZE),
        );
        if (options?.offset != null) {
          params.set("offset", String(Math.max(0, options.offset)));
        }
      }
      return params.toString();
    },
    [bookingSource, customerEmail, sessionId, fromDate, toDate],
  );

  const sourceOptions = useMemo(
    () => Object.entries(BOOKING_SOURCE_LABELS),
    [],
  );

  return {
    bookingSource,
    setBookingSource,
    customerEmail,
    setCustomerEmail,
    sessionId,
    setSessionId,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    buildQuery,
    sourceOptions,
  };
}
